import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  REGISTRATION_STATUS, longDate, money, relative,
  type Registration, type RegistrationStatus,
} from '@lcc/shared';
import { api, qs } from '../lib/api';
import {
  Card, Empty, ErrorNote, Loading, Modal, Pill, SelectField, Stat, Status, TextField,
  errorMessage, useToast,
} from '../components/ui';
import { PageHeader } from '../App';

/**
 * The approval queue.
 *
 * An application is not a member: approving is what creates the member, their
 * membership, their league entries, their waiver signatures and their invoice.
 * Until then nothing about them reaches the roster or the ice.
 */
export function RegistrationsPage() {
  const [status, setStatus] = useState<RegistrationStatus | ''>('pending');
  const [search, setSearch] = useState('');
  const [markingPaid, setMarkingPaid] = useState<Registration | null>(null);
  const [reviewing, setReviewing] = useState<{ reg: Registration; action: 'approve' | 'reject' } | null>(null);

  const list = useQuery({
    queryKey: ['registrations', status, search],
    queryFn: () => api.get<{ data: Registration[]; total: number }>(
      `/registrations${qs({ status: status || undefined, q: search || undefined, perPage: 100 })}`,
    ),
    refetchInterval: 60_000,
  });

  const rows = list.data?.data ?? [];
  const pending = rows.filter((r) => r.status === 'pending');
  const refunds = rows.filter((r) => r.refundDue);
  const owed = pending.reduce((n, r) => n + r.feeCents, 0);
  const paidUpFront = pending.filter((r) => r.paidAt).length;

  return (
    <>
      <PageHeader
        title="Registrations"
        sub={list.data ? `${list.data.total} in view` : undefined}
      />

      <div className="content stack">
        {refunds.length > 0 && (
          <div className="banner banner--error">
            <strong>{refunds.length} rejected registration{refunds.length === 1 ? '' : 's'} owe a refund.</strong>{' '}
            {refunds.map((r) => `${r.fullName} (${money(r.paidAmountCents ?? r.feeCents)})`).join(', ')} —
            refund from the Stripe dashboard, then note it here.
          </div>
        )}

        <div className="grid grid--4">
          <Stat label="Awaiting approval" value={pending.length}
            note={pending.length ? 'nobody is on the ice until approved' : 'all clear'}
            alert={pending.length > 0} />
          <Stat label="Fees represented" value={money(owed)} note="if all are approved" />
          <Stat label="Already paid by card" value={paidUpFront}
            note={paidUpFront ? 'settles on approval' : undefined} />
          <Stat label="Refunds owed" value={refunds.length} alert={refunds.length > 0} />
        </div>

        <Card>
          <div className="row">
            <div style={{ flex: '1 1 240px' }}>
              <TextField label="Search" value={search} onChange={setSearch}
                placeholder="Name or email" />
            </div>
            <div style={{ width: 190 }}>
              <SelectField
                label="Status" value={status} onChange={setStatus}
                options={[
                  { value: '' as const, label: 'All' },
                  ...REGISTRATION_STATUS.map((sv) => ({ value: sv, label: sv })),
                ]}
              />
            </div>
          </div>
        </Card>

        <Card flush>
          {list.isLoading ? <Loading what="Loading registrations" />
            : list.error ? <div style={{ padding: '1rem' }}><ErrorNote error={list.error} /></div>
            : rows.length === 0
              ? <Empty title={status === 'pending' ? 'Nothing waiting' : 'No registrations'}>
                  {status === 'pending'
                    ? 'Every registration has been dealt with.'
                    : 'Registrations arrive from the form on the website.'}
                </Empty>
              : (
                <div className="table-wrap">
                  <table className="t">
                    <thead>
                      <tr>
                        <th>Applicant</th><th>Membership</th><th>Leagues</th>
                        <th>Waivers</th><th>Payment</th><th>Submitted</th>
                        <th>Status</th><th />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.id}>
                          <td>
                            <b>{r.fullName}</b>
                            {r.isReturning && <> <Pill>returning</Pill></>}
                            <br />
                            <span className="small muted">{r.email}</span>
                            {r.phone && <span className="small muted"> · {r.phone}</span>}
                          </td>
                          <td className="small">
                            {r.categoryCode}<br />
                            <span className="muted">{money(r.feeCents)}</span>
                          </td>
                          <td className="small muted">
                            {r.leagueNames?.length ? r.leagueNames.join(', ')
                              : r.isSpare ? <Pill>spare list</Pill> : '—'}
                          </td>
                          <td>
                            {r.waiverAcceptances.length > 0
                              ? <Pill tone="good">{r.waiverAcceptances.length} accepted</Pill>
                              : <Pill tone="bad">none</Pill>}
                          </td>
                          <td className="small">
                            {r.paidAt
                              ? (
                                <Pill tone="good">
                                  {(r as { paymentMethod?: string }).paymentMethod ?? 'paid'}
                                  {' · '}{money(r.paidAmountCents ?? r.feeCents)}
                                </Pill>
                              )
                              : r.paymentChoice === 'now'
                                ? <Pill tone="warn">card started</Pill>
                                : <Pill>at the club</Pill>}
                            {r.refundDue && <> <Pill tone="bad">refund owed</Pill></>}
                          </td>
                          <td className="small nowrap muted">{relative(r.submittedAt)}</td>
                          <td>
                            <Status value={r.status} />
                            {r.memberId && (
                              <><br /><Link className="small" to={`/members/${r.memberId}`}>member</Link></>
                            )}
                          </td>
                          <td className="right nowrap">
                            {r.status === 'pending' ? (
                              <>
                                {/* Most members at a curling club pay at the
                                    desk, so recording that is a first-class
                                    action and not something buried in the
                                    invoice screen afterwards. */}
                                {!r.paidAt && (
                                  <>
                                    <button className="btn btn--sm" type="button"
                                      onClick={() => setMarkingPaid(r)}>
                                      Mark paid
                                    </button>{' '}
                                  </>
                                )}
                                <button className="btn btn--sm btn--accent" type="button"
                                  onClick={() => setReviewing({ reg: r, action: 'approve' })}>
                                  Approve
                                </button>{' '}
                                <button className="btn btn--sm btn--danger" type="button"
                                  onClick={() => setReviewing({ reg: r, action: 'reject' })}>
                                  Decline
                                </button>
                              </>
                            ) : (
                              <span className="small muted">
                                {r.reviewedByName ?? '—'}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
        </Card>
      </div>

      {markingPaid && (
        <MarkPaidDialog
          registration={markingPaid}
          onClose={() => setMarkingPaid(null)}
        />
      )}

      {reviewing && (
        <ReviewModal
          registration={reviewing.reg}
          action={reviewing.action}
          onClose={() => setReviewing(null)}
        />
      )}
    </>
  );
}

function ReviewModal(
  { registration: r, action, onClose }:
  { registration: Registration; action: 'approve' | 'reject'; onClose: () => void },
) {
  const qc = useQueryClient();
  const toast = useToast();
  const [note, setNote] = useState(action === 'approve' ? '' : '');
  const [feeOverride, setFeeOverride] = useState((r.feeCents / 100).toFixed(2));
  const [categoryOverride, setCategoryOverride] = useState(r.categoryCode);

  const run = useMutation({
    mutationFn: () => api.post<{ data: Registration }>(
      `/registrations/${r.id}/${action}`,
      {
        note: note || null,
        overrides: action === 'approve'
          ? {
              categoryCode: categoryOverride !== r.categoryCode ? categoryOverride : undefined,
              feeCents: Math.round(Number(feeOverride) * 100) !== r.feeCents
                ? Math.round(Number(feeOverride) * 100) : undefined,
            }
          : {},
      },
    ),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ['registrations'] });
      void qc.invalidateQueries({ queryKey: ['members'] });
      void qc.invalidateQueries({ queryKey: ['invoices'] });
      void qc.invalidateQueries({ queryKey: ['waivers'] });
      toast.success(action === 'approve'
        ? `${res.data.fullName} is a member. Invoice #${res.data.invoiceNumber} raised.`
        : `${res.data.fullName} declined.`);
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const blocked = action === 'reject' && note.trim().length < 3;

  return (
    <Modal
      title={action === 'approve' ? `Approve ${r.fullName}` : `Decline ${r.fullName}`}
      wide
      onClose={onClose}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>Cancel</button>
          <button
            className={action === 'approve' ? 'btn btn--accent' : 'btn btn--danger'}
            type="button"
            disabled={run.isPending || blocked}
            onClick={() => run.mutate()}
          >
            {run.isPending ? 'Working…'
              : action === 'approve' ? 'Approve and raise the invoice' : 'Decline'}
          </button>
        </>
      }
    >
      <div className="stack">
        {run.error ? <ErrorNote error={run.error} /> : null}

        <Card title="What they submitted">
          <div className="grid grid--2">
            <div className="stack stack--tight small">
              <div><b>{r.fullName}</b></div>
              <div className="muted">{r.email}</div>
              {r.phone && <div className="muted">{r.phone}</div>}
              {(r.address || r.city) && (
                <div className="muted">
                  {[r.address, r.city, r.province, r.postalCode].filter(Boolean).join(', ')}
                </div>
              )}
              {r.dateOfBirth && <div className="muted">Born {longDate(r.dateOfBirth)}</div>}
            </div>
            <div className="stack stack--tight small">
              <div>Membership: <b>{r.categoryCode}</b> · {money(r.feeCents)}</div>
              <div>Leagues: {r.leagueNames?.join(', ') || (r.isSpare ? 'spare list only' : '—')}</div>
              {r.positionPref && <div>Prefers: {r.positionPref}</div>}
              <div>Returning: {r.isReturning ? 'yes' : 'no'}</div>
              {r.emergencyContactName && (
                <div className="muted">
                  Emergency: {r.emergencyContactName} {r.emergencyContactPhone ?? ''}
                </div>
              )}
              {r.accessibilityNotes && (
                <div className="muted">Notes: {r.accessibilityNotes}</div>
              )}
            </div>
          </div>
        </Card>

        <Card title={`Waivers accepted (${r.waiverAcceptances.length})`} flush>
          <div className="table-wrap">
            <table className="t">
              <thead><tr><th>Waiver</th><th>Version</th><th>Accepted</th><th>By</th></tr></thead>
              <tbody>
                {r.waiverAcceptances.map((a) => (
                  <tr key={a.requirementId}>
                    <td>{a.title}</td>
                    <td className="mono">{a.version}</td>
                    <td className="small muted nowrap">{relative(a.acceptedAt)}</td>
                    <td className="small">
                      {a.signedByName ?? r.fullName}
                      {a.isGuardian && <> <Pill tone="warn">guardian</Pill></>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ padding: '.75rem 1.15rem', borderTop: '1px solid var(--border)' }}>
            <p className="small muted" style={{ margin: 0 }}>
              These carry across with their original timestamps — the evidence is of
              when they signed, not when you approved.
            </p>
          </div>
        </Card>

        {action === 'approve' ? (
          <>
            {r.paidAt ? (
              <div className="banner banner--good">
                <strong>Paid by card — {money(r.paidAmountCents ?? r.feeCents)}.</strong>{' '}
                Approving raises the invoice and settles it against this payment.
              </div>
            ) : (
              <div className="banner banner--info">
                Approving raises an <strong>open invoice for {money(r.feeCents)}</strong>,
                due in 30 days. They chose to pay at the club.
              </div>
            )}

            <details>
              <summary className="small muted" style={{ cursor: 'pointer' }}>
                Correct the category or fee before approving
              </summary>
              <div className="grid grid--2" style={{ paddingTop: '.75rem' }}>
                <TextField label="Category code" value={categoryOverride}
                  onChange={setCategoryOverride}
                  hint="e.g. adult, senior, junior" />
                <TextField label="Fee (CAD)" type="number" value={feeOverride}
                  onChange={setFeeOverride}
                  hint="Changes what the invoice is raised for." />
              </div>
            </details>

            <TextField label="Note (optional)" value={note} onChange={setNote}
              hint="Kept on the registration for the record." />
          </>
        ) : (
          <>
            {r.paidAt && (
              <div className="banner banner--error">
                <strong>This applicant has already paid {money(r.paidAmountCents ?? r.feeCents)} by card.</strong>{' '}
                Declining flags a refund as owed — you will need to refund it in Stripe.
              </div>
            )}
            <TextField label="Reason" value={note} onChange={setNote}
              error={blocked && note.length > 0 ? 'Give at least a few words.' : undefined}
              hint="This is emailed to the applicant, so write it for them to read." />
          </>
        )}
      </div>
    </Modal>
  );
}


/**
 * Recording money taken at the club.
 *
 * Cash, a cheque, an e-transfer, or a card on the club's own machine. None of
 * it goes through Stripe, so none of it is charged commission — the dialog
 * says so, because a treasurer should not have to wonder.
 *
 * The reference field is the one that makes the record worth keeping: a cheque
 * number or a terminal receipt number is what answers a query three months
 * later.
 */
function MarkPaidDialog(
  { registration, onClose }: { registration: Registration; onClose: () => void },
) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [method, setMethod] = useState<'cash' | 'cheque' | 'card' | 'etransfer' | 'other'>('cash');
  const [amount, setAmount] = useState(((registration.feeCents ?? 0) / 100).toFixed(2));
  const [reference, setReference] = useState('');

  const save = useMutation({
    mutationFn: () => api.post(`/billing/registrations/${registration.id}/mark-paid`, {
      method,
      amountCents: Math.round(Number(amount) * 100),
      reference: reference.trim() || null,
    }),
    onSuccess: () => {
      toast.success(`${registration.fullName} marked paid.`);
      void queryClient.invalidateQueries({ queryKey: ['registrations'] });
      onClose();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <Modal
      title={`Mark ${registration.fullName} paid`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>Cancel</button>
          <button className="btn btn--primary" type="button" disabled={save.isPending}
            onClick={() => save.mutate()}>
            Record the payment
          </button>
        </>
      }
    >
      <SelectField
        label="How they paid"
        value={method}
        onChange={setMethod}
        options={[
          { value: 'cash', label: 'Cash' },
          { value: 'cheque', label: 'Cheque' },
          { value: 'card', label: 'Card, on the club\u2019s own machine' },
          { value: 'etransfer', label: 'E-transfer' },
          { value: 'other', label: 'Something else' },
        ] as const}
      />
      <TextField
        label="Amount"
        value={amount}
        onChange={setAmount}
        type="number"
        hint="Change it for a part payment or an agreed discount."
      />
      <TextField
        label="Reference"
        value={reference}
        onChange={setReference}
        hint="Cheque number, receipt number \u2014 whatever makes this traceable later."
      />
      <p className="small">
        This is money the club took directly. It does not go through Stripe and
        is not charged platform commission. If the applicant has already been
        approved, their invoice is settled at the same time.
      </p>
    </Modal>
  );
}
